import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthShell } from "./auth-shell";
import { TwoFactorChallenge } from "./two-factor-challenge";
import type { TwoFactorChallengeInfo } from "./two-factor";

const SSO_LOGIN_ERRORS: Record<string, string> = {
  zoom_not_configured: "Sign in with Zoom is not configured on this server.",
  zoom_denied: "Zoom sign-in was cancelled or denied.",
  zoom_expired: "Zoom sign-in expired. Please try again.",
  zoom_failed: "Zoom sign-in failed. Please try again.",
  zoom_login_email: "Your Zoom account must have an email address.",
  google_not_configured:
    "Sign in with Google is not configured on this server.",
  google_denied: "Google sign-in was cancelled or denied.",
  google_expired: "Google sign-in expired. Please try again.",
  google_failed: "Google sign-in failed. Please try again.",
  google_login_email: "Your Google account must have an email address.",
  google_login_sub: "Google sign-in did not return an account id.",
  google_login_unverified: "Your Google account email must be verified.",
  microsoft_not_configured:
    "Sign in with Microsoft is not configured on this server.",
  microsoft_denied: "Microsoft sign-in was cancelled or denied.",
  microsoft_expired: "Microsoft sign-in expired. Please try again.",
  microsoft_failed: "Microsoft sign-in failed. Please try again.",
  microsoft_login_email: "Your Microsoft account must have an email address.",
  microsoft_login_sub: "Microsoft sign-in did not return an account id.",
  email_not_verified:
    "Please verify your email before signing in. Check your inbox for the verification link.",
  email_unverified_merge:
    "An account with this email already exists but isn't verified. Check your inbox for the verification link, or sign in with your password.",
  google_oauth_not_configured:
    "Sign in with Google is not configured on this server.",
  google_token_exchange_failed: "Google sign-in failed. Please try again.",
  google_userinfo_failed: "Google sign-in failed. Please try again.",
};

function ZoomMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.5 6.75A2.25 2.25 0 0 1 6.75 4.5h6A2.25 2.25 0 0 1 15 6.75v6a2.25 2.25 0 0 1-2.25 2.25h-6A2.25 2.25 0 0 1 4.5 12.75v-6Zm11.25 2.1 3.3-2.2a.75.75 0 0 1 1.2.6v7.5a.75.75 0 0 1-1.2.6l-3.3-2.2v-4.3Z"
      />
    </svg>
  );
}

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M2.5 2.5h9v9h-9z" />
      <path fill="#7FBA00" d="M12.5 2.5h9v9h-9z" />
      <path fill="#00A4EF" d="M2.5 12.5h9v9h-9z" />
      <path fill="#FFB900" d="M12.5 12.5h9v9h-9z" />
    </svg>
  );
}

export function LoginPage() {
  const { user, organizations, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resendState, setResendState] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoomLoginEnabled, setZoomLoginEnabled] = useState(false);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
  const [microsoftLoginEnabled, setMicrosoftLoginEnabled] = useState(false);
  const [challenge, setChallenge] = useState<TwoFactorChallengeInfo | null>(
    null,
  );
  const [challengeError, setChallengeError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // SSO logins with 2FA redirect back here with ?challenge=....
  useEffect(() => {
    const challengeId = params.get("challenge");
    if (!challengeId || challenge) return;
    let cancelled = false;
    api<{ methods: string[] }>("/api/auth/2fa/challenge/methods", {
      method: "POST",
      body: JSON.stringify({ challengeId }),
    })
      .then((info) => {
        if (!cancelled) setChallenge({ challengeId, methods: info.methods });
      })
      .catch(() => {
        if (!cancelled)
          setChallengeError("That sign-in challenge expired. Sign in again.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || user) return;
    emailRef.current?.focus();
  }, [loading, user]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api<{ enabled: boolean }>("/api/auth/zoom")
        .then((status) => {
          if (!cancelled) setZoomLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setZoomLoginEnabled(false);
        }),
      api<{ enabled: boolean }>("/api/auth/google")
        .then((status) => {
          if (!cancelled) setGoogleLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setGoogleLoginEnabled(false);
        }),
      api<{ enabled: boolean }>("/api/auth/microsoft")
        .then((status) => {
          if (!cancelled) setMicrosoftLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setMicrosoftLoginEnabled(false);
        }),
    ]);
    return () => {
      cancelled = true;
    };
  }, []);

  const oauthError = useMemo(() => {
    const code = params.get("error");
    if (!code) return null;
    if (code === "zoom_not_configured" && !zoomLoginEnabled) return null;
    if (
      (code === "google_not_configured" ||
        code === "google_oauth_not_configured") &&
      !googleLoginEnabled
    ) {
      return null;
    }
    if (code === "microsoft_not_configured" && !microsoftLoginEnabled)
      return null;
    return SSO_LOGIN_ERRORS[code] ?? "Sign-in failed. Please try again.";
  }, [params, zoomLoginEnabled, googleLoginEnabled, microsoftLoginEnabled]);

  if (!loading && user) {
    const first = organizations[0]?.id;
    return <Navigate to={first ? "/" : "/onboarding"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setErrorCode(null);
    setResendState(null);
    try {
      const pending = await signIn(email, password);
      if (pending) {
        setChallenge(pending);
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
      setErrorCode(
        err && typeof err === "object" && "code" in err
          ? String((err as { code?: unknown }).code ?? "")
          : null,
      );
    } finally {
      setBusy(false);
    }
  }

  async function onResendVerification() {
    setResendBusy(true);
    setResendState(null);
    try {
      await api("/api/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setResendState("Verification email sent. Check your inbox.");
    } catch (err) {
      setResendState(err instanceof Error ? err.message : "Could not resend.");
    } finally {
      setResendBusy(false);
    }
  }

  const oauthSuggestsResend =
    oauthError === SSO_LOGIN_ERRORS.email_not_verified ||
    oauthError === SSO_LOGIN_ERRORS.email_unverified_merge;
  const apiSuggestsResend =
    errorCode === "EMAIL_NOT_VERIFIED" ||
    errorCode === "EMAIL_UNVERIFIED_MERGE_BLOCKED";
  const showResend = oauthSuggestsResend || apiSuggestsResend;

  const showSso =
    zoomLoginEnabled || googleLoginEnabled || microsoftLoginEnabled;

  if (challenge || params.get("challenge")) {
    return (
      <AuthShell>
        {challenge ? (
          <TwoFactorChallenge
            challenge={challenge}
            onDone={() => navigate("/")}
            onRestart={() => {
              setChallenge(null);
              navigate("/login", { replace: true });
            }}
          />
        ) : (
          <p className="text-sm text-rs-ink-muted" role="status">
            {challengeError ?? "Loading your second-factor methods…"}
          </p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      belowPanel={
        <p className="text-center text-sm text-rs-ink-muted">
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            className="font-medium underline-offset-2 hover:underline"
          >
            Create Account
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Input
          ref={emailRef}
          id="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          aria-label="Email"
        />
        <div className="space-y-1.5">
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              aria-label="Password"
              className="pr-10"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-200"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide Password" : "Show Password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          </div>
          <Link to="/forgot-password" className="rs-auth-forgot">
            Forgot Password
          </Link>
        </div>
        {oauthError || error ? (
          <div className="space-y-2">
            <p className="text-sm text-rs-danger" role="alert">
              {error ?? oauthError}
            </p>
            {showResend ? (
              <div className="space-y-1.5">
                <p className="text-xs text-rs-ink-muted">
                  Enter your email above, then resend the verification link.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={resendBusy || !email.trim()}
                  onClick={() => void onResendVerification()}
                >
                  {resendBusy ? "Sending…" : "Resend verification email"}
                </Button>
                {resendState ? (
                  <p className="text-sm text-rs-ink-muted" role="status">
                    {resendState}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <Button
          className="rs-auth-submit rs-auth-submit-block"
          type="submit"
          disabled={busy}
        >
          {busy ? "Signing In…" : "Sign In"}
        </Button>
      </form>
      {showSso ? (
        <div className="rs-auth-alt mt-2">
          <div className="rs-auth-divider" role="separator" aria-label="or">
            <span>OR</span>
          </div>
          <div className="rs-auth-sso-row">
            {googleLoginEnabled ? (
              <a
                href="/oauth/login/google/start"
                className="rs-auth-sso"
                aria-label="Sign In with Google"
                title="Sign In with Google"
              >
                <GoogleMark className="rs-auth-sso-icon" />
                <span className="rs-auth-sso-label">Google</span>
              </a>
            ) : null}
            {microsoftLoginEnabled ? (
              <a
                href="/oauth/login/microsoft/start"
                className="rs-auth-sso"
                aria-label="Sign In with Microsoft"
                title="Sign In with Microsoft"
              >
                <MicrosoftMark className="rs-auth-sso-icon" />
                <span className="rs-auth-sso-label">Microsoft</span>
              </a>
            ) : null}
            {zoomLoginEnabled ? (
              <a
                href="/oauth/login/start"
                className="rs-auth-sso"
                aria-label="Sign In with Zoom"
                title="Sign In with Zoom"
              >
                <ZoomMark className="rs-auth-sso-icon rs-auth-sso-icon-zoom" />
                <span className="rs-auth-sso-label">Zoom</span>
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </AuthShell>
  );
}
